import { useUserIdentity } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import ScaleIcon from '@src/svgs/ScaleIcon';
import UpDownIcon from '@src/svgs/UpDownIcon';
import { AspectRatio } from '@src/types.ts/video';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { observer } from 'mobx-react-lite';
import React from 'react';
import styled from 'styled-components';

import { FIT_DEFAULT } from '../../config/videoSettings';
import { numericalInputStyles } from '../../styles/mainStyle';
import SliderSelect from '../common/PlainSlider';
import { ElementAnimationSettings } from './ElementAnimationSettings';
import { GenericAnimationSettings } from './GenericAnimationSettings';
import { PropertyCaption } from './PropertyCaption';
import { PropertyNumericalInput } from './PropertyNumericalInput';
import useLayoutAwareVideoSettingsState from './useLayoutAwareVideoSettingsState';
import { VideoFitPropertySelect } from './VideoFitPropertySelect';
import { VideoPreset } from './VideoPreset';

import type { ElementState } from '../../renderer/ElementState';
import type { EditorStoreLike } from '@src/utility/compositionSettings';

interface VideoSettingsProps {
  activeElement: ElementState;
  editorStore: EditorStoreLike;
}

export const VideoSettings: React.FC<VideoSettingsProps> = observer(props => {
  const videoCreator = useVideoCreatorStore();
  const userIdentity = useUserIdentity();
  const {
    enableStackedLayouts,
    creatorStudioTimelineEnableFit13,
    creatorStudioTimelineEnableFitSplitHorizontal11,
    creatorStudioTimelineEnableFitSplitHorizontal13,
    creatorStudioSettingsAnimationAndAudioFadeOnly,
  } = useFlagsCombination(userIdentity.currentRole);

  const {
    activeTimelineVideoElement, // layout child's hidden parent or regular active
    displayElement, // layout child or regular active
    isLayoutComposition,
    sourceUrl,
    xValue,
    yValue,
    modifyPropertyWithSlider,
    handleChangeScale,
    updateTimelineVideoProperty,
  } = useLayoutAwareVideoSettingsState({
    activeElement: props.activeElement,
    editorStore: props.editorStore,
  });

  if (!props.activeElement) {
    return null;
  }

  const isBroll = !videoCreator.isOriginalVideoElement(
    activeTimelineVideoElement.source,
  );

  const is916 = videoCreator.currentVideo?.aspectRatio === AspectRatio.AR_9_16;

  // When this flag is on, the panel is reduced to Enter Animation (+ length)
  // and Audio Fade In/Out only — every other control is hidden.
  const showFullSettings = !creatorStudioSettingsAnimationAndAudioFadeOnly;

  // For the original video element (not b-roll), alignment sliders only apply
  // to vertical (9:16) scenes and live in a "Current Scene only" section gated
  // behind showFullSettings; scale/zoom is hidden entirely.
  const showAlignmentSliders = showFullSettings && (isBroll || is916);
  const showZoom = showFullSettings && isBroll;

  return (
    <VideoSettingsContainer>
      {showFullSettings && isBroll && (
        <>
          <VideoPreset activeElement={displayElement} url={sourceUrl} />
          <ElementAnimationSettings
            activeElement={activeTimelineVideoElement}
            editorStore={props.editorStore}
          />
        </>
      )}

      {showFullSettings && (
        <GroupContent>
          <Item>
            <PropertyCaption>Start</PropertyCaption>
            <PropertyNumericalInput
              activeElement={activeTimelineVideoElement}
              propertyName="time"
              defaultValue="auto"
              unit="s"
              disabled={!isBroll}
              customStyles={numericalInputStyles}
              getInputValue={async (property: string, value: string) => {
                await updateTimelineVideoProperty({ [property]: value });
              }}
            />
          </Item>

          <Item>
            <PropertyCaption>Duration</PropertyCaption>
            <PropertyNumericalInput
              activeElement={activeTimelineVideoElement}
              propertyName="duration"
              defaultValue="auto"
              unit="s"
              disabled={!isBroll}
              customStyles={numericalInputStyles}
              getInputValue={async (property: string, value: string) => {
                await updateTimelineVideoProperty({ [property]: value });
              }}
            />
          </Item>

          {isBroll && (
            <Item>
              {/* Layout/Fit dropdown - show all options */}
              <PropertyCaption>
                {isLayoutComposition ? 'Layout Type' : 'Fit'}
              </PropertyCaption>
              <VideoFitPropertySelect
                activeElement={activeTimelineVideoElement}
                propertyName="fit"
                defaultValue={isLayoutComposition ? 'stacked-1-1' : FIT_DEFAULT}
                options={[
                  { caption: 'Cover', value: 'cover' },
                  { caption: 'Contain', value: 'contain' },
                  ...(enableStackedLayouts &&
                  videoCreator.currentVideo?.aspectRatio === AspectRatio.AR_9_16
                    ? [
                        ...(creatorStudioTimelineEnableFitSplitHorizontal11
                          ? [
                              {
                                caption: 'Stacked 1:1',
                                value: 'stacked-1-1',
                              },
                            ]
                          : []),
                        ...(creatorStudioTimelineEnableFit13 &&
                        creatorStudioTimelineEnableFitSplitHorizontal13
                          ? [
                              {
                                caption: 'Stacked 1:3',
                                value: 'stacked-1-3',
                              },
                            ]
                          : []),
                      ]
                    : []),
                ]}
              />
            </Item>
          )}

          {/* Add a second fit dropdown for the child video element when we're inside a composition */}
          {/* {childVideoElement && (
            <Item>
              <PropertyCaption>Inner Video Fit</PropertyCaption>
              <VideoFitPropertySelect
                activeElement={childVideoElement}
                propertyName="fit"
                defaultValue={FIT_DEFAULT}
                options={[
                  { caption: 'Cover', value: 'cover' },
                  { caption: 'Contain', value: 'contain' },
                ]}
              />
            </Item>
          )} */}

          {isBroll && (
            <Item>
              <PropertyCaption>Volume</PropertyCaption>
              <PropertyNumericalInput
                activeElement={activeTimelineVideoElement}
                propertyName="volume"
                defaultValue="100"
                unit="%"
                customStyles={numericalInputStyles}
                getInputValue={async (property: string, value: string) => {
                  await updateTimelineVideoProperty({ [property]: value });
                }}
              />
            </Item>
          )}
        </GroupContent>
      )}

      <GenericAnimationSettings
        hideFadeAnimations
        activeElement={activeTimelineVideoElement}
        editorStore={props.editorStore}
      />

      <GroupContent>
        <Item>
          <PropertyCaption>Audio Fade In</PropertyCaption>
          <PropertyNumericalInput
            activeElement={activeTimelineVideoElement}
            propertyName="audio_fade_in"
            defaultValue="0"
            unit="s"
            customStyles={numericalInputStyles}
            getInputValue={async (property: string, value: string) => {
              await updateTimelineVideoProperty({
                [property]: parseFloat(value),
              });
            }}
          />
        </Item>
        <Item>
          <PropertyCaption>Audio Fade Out</PropertyCaption>
          <PropertyNumericalInput
            activeElement={activeTimelineVideoElement}
            propertyName="audio_fade_out"
            defaultValue="0"
            unit="s"
            customStyles={numericalInputStyles}
            getInputValue={async (property: string, value: string) => {
              await updateTimelineVideoProperty({
                [property]: parseFloat(value),
              });
            }}
          />
        </Item>
      </GroupContent>
      {showAlignmentSliders && (
        <SliderGroup>
          {!isBroll && (
            <SliderGroupHeader>Current Scene only</SliderGroupHeader>
          )}
          <SliderItem>
            <PropertyCaption>Horizontal Alignment</PropertyCaption>
            <SliderAndIcon>
              <UpDownIcon rotate={90} />
              <SliderSelect
                getValue={newValue => modifyPropertyWithSlider('x', newValue)}
                defaultValue={50}
                currValue={xValue}
                min={0}
                max={100}
                ticks={[
                  { value: 0, label: '0%' },
                  { value: 50, label: '50%' },
                  { value: 100, label: '100%' },
                ]}
                unit="%"
              />
            </SliderAndIcon>
          </SliderItem>
          <SliderItem>
            <PropertyCaption>Vertical Alignment</PropertyCaption>
            <SliderAndIcon>
              <UpDownIcon />
              <SliderSelect
                getValue={newValue => modifyPropertyWithSlider('y', newValue)}
                defaultValue={50}
                currValue={yValue}
                min={0}
                max={100}
                ticks={[
                  { value: 0, label: '0%' },
                  { value: 50, label: '50%' },
                  { value: 100, label: '100%' },
                ]}
                unit="%"
              />
            </SliderAndIcon>
          </SliderItem>
          {showZoom && (
            <SliderItem>
              <PropertyCaption>Zoom In/Out</PropertyCaption>
              <SliderAndIcon>
                <ScaleIcon />
                <SliderSelect
                  getValue={newValue => handleChangeScale(newValue)}
                  defaultValue={100}
                  currValue={(() => {
                    const element = displayElement;
                    if (
                      element &&
                      element.source &&
                      element.source.width !== undefined
                    ) {
                      const width = element.source.width.toString();
                      const widthVal = parseFloat(width);
                      return !isNaN(widthVal) ? widthVal : 100;
                    }
                    return 100;
                  })()}
                  min={20}
                  max={500}
                  midpoint={100}
                  ticks={[{ value: 100, label: '100%' }]}
                  unit="%"
                />
              </SliderAndIcon>
            </SliderItem>
          )}
        </SliderGroup>
      )}
    </VideoSettingsContainer>
  );
});

const VideoSettingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-gutter: stable both-edges;
  padding-left: var(--spacing-sm);
  padding-right: var(--spacing-sm);
  padding-bottom: 20px;
  box-sizing: border-box;
`;

export const GroupContent = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 10px;
`;

export const Item = styled.div``;

const SliderAndIcon = styled.div`
  display: flex;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 12px;
  gap: 5px;
  align-items: center;
  height: 15px;
`;

const SliderGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SliderGroupHeader = styled.div`
  margin: 15px 0 5px 0;
  color: var(--text-muted);
  font-weight: var(--font-weight-semi-bold);
  font-size: var(--text-xs);
  letter-spacing: 0.5px;
`;

const SliderItem = styled.div``;
